<template>
    <div class="skill" v-if="skill">
        <page-image-top :title="$t(`skills.${skill.name}.content.title`)"
                        :breadcrumbs="breadcrumbs"
                        :image="'/static/backgrounds/table-top.jpeg'" />

        <div class="section">
            <div class="container">
                <div class="columns is-vcentered">
                    <div class="column is-6" style="padding: auto 100px">
                        <h2 class="is-size-4">{{$t(`skills.${skill.name}.content.firstSection.title`)}}</h2><br>
                        <p v-html="$t(`skills.${skill.name}.content.firstSection.firstParagraph`)" /><br>
                        <p v-html="$t(`skills.${skill.name}.content.firstSection.secondParagraph`)" /><br>
                        <p v-html="$t(`skills.${skill.name}.content.firstSection.thirdParagraph`)" />
                    </div>

                    <div class="column is-6">
                        <figure class="image">
                            <img :src="$t(`skills.${skill.name}.imageUrl`)" :alt="$t(`skills.${skill.name}.content.title`)">
                        </figure>
                    </div>
                </div>
            </div>
        </div>

        <div class="section has-background-light">
            <div class="container">
                <div class="columns">
                    <div class="column is-6">
                        <h2 class="is-size-4">Hvordan foregår en opgave med <span class="is-lowercase">{{$t(`skills.${skill.name}.name`)}}</span>?</h2><br>

                        <ul class="steps has-content-centered is-medium is-vertical">
                            <li class="steps-segment">
                                <span class="steps-marker"><b-icon icon="clipboard-text-play" /></span>

                                <div class="steps-content">
                                    <p class="is-size-6"><strong>Opgaven:</strong> Du har en opgave som du ønsker løst til en overkommelig pris.</p>
                                </div>
                            </li>

                            <li class="steps-segment">
                                <span class="steps-marker"><b-icon icon="clipboard-check-outline" /></span>

                                <div class="steps-content">
                                    <p class="is-size-6"><strong>Forhandling:</strong> Via Taskflex’ beskedsystem bliver I enige om opgavens detaljer og pris.</p>
                                </div>
                            </li>

                            <li class="steps-segment">
                                <span class="steps-marker"><b-icon icon="check-outline" /></span>

                                <div class="steps-content">
                                    <p class="is-size-6"><strong>Levering:</strong> Når opgaven er løst sendes en faktura på det aftalte beløb, og den studerende modtager sin betaling.</p>
                                </div>
                            </li>
                        </ul>

                        <div class="has-text-centered">
                            <router-link class="button is-primary is-" :to="{ name: 'students.search', params: { skill: skill.name } }">
                                Find studerende der laver&nbsp;<span class="is-lowercase">{{$t(`skills.${skill.name}.content.title`)}}</span>
                            </router-link>
                        </div>
                        <br class="is-hidden-desktop">
                    </div>

                    <div class="column is-6">
                        <h2 class="is-size-4">{{$t(`skills.${skill.name}.content.secondSection.title`)}}</h2><br>
                        <p v-html="$t(`skills.${skill.name}.content.secondSection.firstParagraph`)" /><br>
                        <p v-html="$t(`skills.${skill.name}.content.secondSection.secondParagraph`)" /><br>
                        <p v-html="$t(`skills.${skill.name}.content.secondSection.thirdParagraph`)" />
                    </div>
                </div>
            </div>
        </div>
        <div class="section">
            <div class="container">
                <div class="columns is-vcentered">
                    <div class="column is-6">
                        <h2 class="is-size-4">{{$t(`skills.${skill.name}.content.thirdSection.title`)}}</h2><br>
                        <p v-html="$t(`skills.${skill.name}.content.thirdSection.firstParagraph`)" /><br>
                        <p v-html="$t(`skills.${skill.name}.content.thirdSection.secondParagraph`)" /><br>
                        <p v-html="$t(`skills.${skill.name}.content.thirdSection.thirdParagraph`)" />
                    </div>

                    <div class="column is-6">
                        <div class="pricing-table is-comparative" v-if="!isLoadingLevels && !isLoadingSkills">
                            <div class="pricing-plan is-features">
                                <div class="plan-header">Niveauer</div>

                                <div class="plan-price">&nbsp;</div>

                                <div class="plan-items has-text-weight-bold">
                                    <div class="plan-item" v-for="level in levels">
                                        {{$t(`levels.${level.name}.name`)}}<level-icon size="is-small" :level="level" />
                                    </div>
                                </div>

                                <div class="plan-footer"></div>
                            </div>

                            <div class="pricing-plan level-plan">
                                <div class="plan-header">
                                    {{$t(`skills.${skill.name}.name`)}}
                                </div>

                                <div class="plan-price">&nbsp;</div>

                                <div class="plan-items">
                                    <div class="plan-item" v-for="level in levels">
                                        <span class="is-hidden-tablet has-text-weight-bold">
                                            {{$t(`levels.${level.name}.name`)}}<level-icon size="is-small" :level="level" />:
                                        </span>
                                        &nbsp;<price :pay-grade="paygrade(level)"/>
                                    </div>
                                </div>

                                <div class="plan-footer">
                                    <small>Alle priser er eks. moms</small>
                                </div>
                            </div>

                        </div>
                        <loader v-else text="Indlæser.." style="min-height: 300px" />

                    </div>
                </div>
            </div>
        </div>

        <div class="hero is-primary">
            <div class="hero-body">
                <h1 class="title is-size-4 is-spaced has-text-centered is-uppercase">
                    Klar til at få opgaven løst?
                </h1>

                <p class="subtitle has-text-centered">
                    <router-link class="button is-primary is-inverted is-outlined is-medium" :to="{ name: 'students.search', params: { skill: skill.name } }">
                        Find studerende der kan hjælpe med&nbsp;<span class="is-lowercase">{{$t(`skills.${skill.name}.name`)}}</span>
                    </router-link>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import LevelIcon from '@/taskflex/levels/components/LevelIcon';
    import Loader from '@/global/components/Loader';
    import PageImageTop from '@/global/components/PageImageTop';
    import Price from '@/taskflex/skills/components/Price';

    export default {
        metaInfo() {
            return {
                title: this.skillGroup ? this.$t(`skills.${this.skill.name}.content.firstSection.title`) : 'Indlæser',
                meta: [
                    {
                        vmid: 'description',
                        name: 'description',
                        content: this.skillGroup ? this.$t(`skills.${this.skill.name}.content.metaDescription`) : 'Indlæser',
                    },
                ],
            };
        },

        computed: {
            ...mapGetters('levels', {
                levels: 'levels',
                isLoadingLevels: 'isLoading',
            }),

            ...mapGetters('skills', {
                skills: 'skills',
                skillGroups: 'skillGroups',
                isLoadingSkills: 'isLoading',
            }),

            skill() {
                const { skill: name } = this.$route.params;

                return this.skills.find(skill => skill.name === name);
            },

            skillGroup() {
                const { group } = this.$route.params;

                return this.skillGroups.find(skillGroup => skillGroup.name === group);
            },

            breadcrumbs() {
                return [
                    {
                        title: this.$t('pages.home.title'),
                        route: { name: 'home' },
                    },
                    {
                        title: this.$t('pages.groups-overview.title'),
                        route: { name: 'skills.groups-overview' },
                    },
                    {
                        title: this.$t(`skills.groups.${this.skillGroup.name}.name`),
                        route: { name: 'skills.group', params: { group: this.skillGroup.name } },
                    },
                    {
                        title: this.$t(`skills.${this.skill.name}.name`),
                        route: { name: 'skills.skill', params: { group: this.skillGroup.name, skill: this.skill.name } },
                    },
                ];
            },
        },

        mounted() {
            if (!this.skillGroups.length) {
                this.$store.dispatch('skills/loadSkillGroups');
            }

            if (!this.levels.length) {
                this.$store.dispatch('levels/loadLevels');
            }
        },

        methods: {
            paygrade(level) {
                return this.skill.paygrades.find(paygrade => paygrade.level_id === level.id);
            },
        },

        components: {
            LevelIcon,
            Loader,
            PageImageTop,
            Price,
        },
    };
</script>




// WEBPACK FOOTER //
// src/taskflex/skills/pages/Skill.vue